// Copyright (c) 2025 Signal from Noise, Inc. All rights reserved.
//
// This software is the confidential and proprietary information of
// Signal from Noise, Inc. You shall not disclose such Confidential Information
// and shall use it only in accordance with the terms of a separate license
// agreement entered into with Signal from Noise, Inc.
//
// Authors: /AUTHORS

import { getContentTypes, supportedExtensions } from "@repo/mime";
import {
  Asset,
  type AssetService,
  createCompleteAssetUploadRequest,
  createCreateAssetRequest,
  createGetAssetsRequest,
  createUploadAssetRequest,
  timestampToDateOptional,
  useAssetClient,
  useCallOptions,
  withBearerToken,
} from "@storytell/cp-client";
import type { AssetWithActions } from "@storytell/cp-client/src/gen/assets/v3/asset_service_pb";
import { createSignal, onCleanup } from "solid-js";
import { isServer } from "solid-js/web";
import SparkMD5 from "spark-md5";

/**
 * File upload strategy based on size threshold (10MB)
 */
export type UploadStrategy = "DIRECT" | "SIGNED_URL";

/**
 * Upload progress state for tracking upload status
 */
export interface UploadProgress {
  /** Total file size in bytes */
  total: number;
  /** Bytes uploaded so far */
  loaded: number;
  /** Progress percentage (0-100) */
  percentage: number;
  /** Current upload speed in bytes/second */
  speed: number;
  /** Estimated time remaining in seconds */
  timeRemaining: number;
}

/**
 * File upload state for a single file
 */
export interface FileUploadState {
  /** Unique upload ID */
  id: string;
  /** File being uploaded */
  file: File;
  /** Project ID for the upload */
  projectId: string;
  /** Current upload status */
  status: "pending" | "uploading" | "processing" | "completed" | "error";
  /** Upload strategy used for this file */
  strategy: UploadStrategy;
  /** Upload progress information */
  progress: UploadProgress;
  /** Error message if upload failed */
  error?: string;
  /** Asset ID once created */
  assetId?: string;
  /** Signed URL for large file uploads */
  signedUrl?: string;
  /** Upload headers for signed URL uploads */
  uploadHeaders?: Record<string, string>;
  /** Upload expiration time */
  expiresAt?: Date;
  /** Asset lifecycle state for tracking processing status */
  lifecycleState?: Asset.AssetLifecycleState;
  /** Job status information */
  jobStatus?: string;
  /** MD5 hash of the file for duplicate detection */
  md5Hash?: string;
}

/**
 * Configuration for file upload hook
 */
export interface FileUploadConfig {
  /** Maximum file size in bytes (default: 100MB) */
  maxFileSize?: number;
  /** Allowed file types (MIME types) */
  allowedTypes?: string[];
  /** Allowed file extensions (fallback for unknown MIME types) */
  allowedExtensions?: string[];
  /** Maximum number of concurrent uploads */
  maxConcurrent?: number;
  /** Chunk size for large file uploads */
  chunkSize?: number;
  /** Function to get current authentication token */
  getAuthToken?: () => string | undefined;
  /** Callback called when an asset is created and gets an ID */
  onAssetCreated?: (assetId: string, asset: AssetWithActions) => void;
  /** Callback called when an asset status changes */
  onAssetStatusChanged?: (
    assetId: string,
    status: "uploading" | "processing" | "completed" | "error",
    asset: AssetWithActions | null,
  ) => void;
  /** Callback called when an asset becomes ready */
  onAssetReady?: (assetId: string) => void;
}

/**
 * File upload hook return type
 */
export interface UseFileUploadReturn {
  /** Current upload states for all files */
  uploads: () => FileUploadState[];
  /** Whether any uploads are in progress */
  isUploading: () => boolean;
  /** Total upload progress across all files */
  totalProgress: () => number;
  /** Upload files */
  uploadFiles: (files: File[], projectId: string) => Promise<void>;
  /** Remove a file from upload queue */
  removeFile: (uploadId: string) => void;
  /** Retry a failed upload */
  retryUpload: (uploadId: string) => Promise<void>;
  /** Clear all completed uploads */
  clearCompleted: () => void;
  /** Cancel an active upload */
  cancelUpload: (uploadId: string) => void;
}

/**
 * Get default configuration with lazy MIME type resolution
 */
function getDefaultConfig(): FileUploadConfig {
  return {
    maxFileSize: 100 * 1024 * 1024, // 100MB
    allowedTypes: getContentTypes(),
    allowedExtensions: supportedExtensions,
    maxConcurrent: 3,
    chunkSize: 8 * 1024 * 1024, // 8MB chunks
    getAuthToken: () => undefined,
  };
}

/**
 * Size threshold for upload strategy (10MB)
 */
const SIZE_THRESHOLD = 10 * 1024 * 1024;

/**
 * Generate unique upload ID
 */
function generateUploadId(): string {
  return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Calculate MD5 checksum for file integrity checking with GCP Cloud Storage
 * Lifted and shifted from @core/lib/files for v3 compatibility
 */
export const computeMD5Checksum = async (file: File): Promise<string> => {
  if (isServer) {
    throw new Error("MD5 checksum calculation is not available on the server");
  }
  const spark = SparkMD5.ArrayBuffer.hash(await file.arrayBuffer());
  return hexToBase64(spark);
};

// Utility functions for hex <-> bytes conversion
function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = Number.parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

// Convert binary string to base64
function binaryToBase64(binary: string): string {
  return btoa(binary);
}

// Convert hex to base64
function hexToBase64(hexString: string): string {
  const bytes = hexToBytes(hexString);
  const binaryString = Array.from(bytes)
    .map((byte) => String.fromCharCode(byte))
    .join("");
  return binaryToBase64(binaryString);
}

/**
 * Get file extension from filename
 */
function getFileExtension(filename: string): string {
  const extension = filename.toLowerCase().split(".").pop();
  return extension ? `.${extension}` : "unknown";
}

/**
 * Check if file extension is allowed
 */
function isAllowedExtension(filename: string, allowedExtensions?: string[]): boolean {
  if (!allowedExtensions) return false;
  const extension = getFileExtension(filename);
  return extension !== "unknown" && allowedExtensions.includes(extension);
}

/**
 * Validate file against configuration
 */
function validateFile(file: File, config: FileUploadConfig): string | null {
  const defaultConfig = getDefaultConfig();

  if (file.size > (config.maxFileSize || defaultConfig.maxFileSize!)) {
    return `File size exceeds maximum allowed size of ${(config.maxFileSize || defaultConfig.maxFileSize!) / (1024 * 1024)}MB`;
  }

  const allowedTypes = config.allowedTypes || defaultConfig.allowedTypes!;

  // Check MIME type first, then fall back to file extension
  const isValidType =
    allowedTypes.includes(file.type) ||
    (file.type === "" && isAllowedExtension(file.name, config.allowedExtensions || defaultConfig.allowedExtensions));

  if (!isValidType) {
    return `File type '${file.type || getFileExtension(file.name)}' is not supported`;
  }

  return null;
}

/**
 * Check if a file is a duplicate based on MD5 hash, size, and name
 */
async function isDuplicateFile(file: File, existingUploads: FileUploadState[]): Promise<boolean> {
  try {
    const fileHash = await computeMD5Checksum(file);

    return existingUploads.some((upload) => {
      // First check: if we have stored MD5 hashes, compare them for exact duplicate detection
      if (upload.md5Hash && upload.md5Hash === fileHash) {
        console.log(`Duplicate file detected by MD5 hash: ${file.name}`);
        return true;
      }

      // Second check: basic file properties (fallback or when MD5 not available)
      const isSameBasicProps =
        upload.file.name === file.name && upload.file.size === file.size && upload.file.type === file.type;

      if (isSameBasicProps) {
        console.log(`Likely duplicate file detected by name/size/type: ${file.name}`);
        return true;
      }

      return false;
    });
  } catch (error) {
    console.warn("Failed to compute MD5 for duplicate check:", error);
    // Fallback to basic comparison if MD5 fails
    const isDuplicate = existingUploads.some(
      (upload) => upload.file.name === file.name && upload.file.size === file.size && upload.file.type === file.type,
    );

    if (isDuplicate) {
      console.log(`Likely duplicate file detected by name/size/type (fallback): ${file.name}`);
    }

    return isDuplicate;
  }
}

/**
 * Hook for handling file uploads with progress tracking and size-based routing
 */
export function useFileUpload(userConfig: FileUploadConfig = {}): UseFileUploadReturn {
  const config = { ...getDefaultConfig(), ...userConfig };
  const [uploads, setUploads] = createSignal<FileUploadState[]>([]);

  // Return server-safe defaults during SSR
  if (isServer) {
    return {
      uploads,
      isUploading: () => false,
      totalProgress: () => 0,
      uploadFiles: async () => {},
      removeFile: () => {},
      retryUpload: async () => {},
      clearCompleted: () => {},
      cancelUpload: () => {},
    };
  }

  // Track active upload requests for cancellation
  const activeRequests = new Map<string, AbortController>();

  // Track active polling timers for cleanup
  const activePollingTimers = new Map<string, number>();

  // Global polling timer for bulk polling all active assets
  let globalPollingTimer: number | null = null;

  /**
   * Calculate total progress across all uploads
   */
  const totalProgress = () => {
    const allUploads = uploads();
    if (allUploads.length === 0) return 0;

    const totalProgress = allUploads.reduce((sum, upload) => sum + upload.progress.percentage, 0);
    return Math.round(totalProgress / allUploads.length);
  };

  /**
   * Check if any uploads are in progress
   */
  const isUploading = () => {
    return uploads().some((upload) => upload.status === "uploading" || upload.status === "processing");
  };

  /**
   * Create asset via protobuf client
   */
  async function createAsset(
    file: File,
    md5Hash: string,
    projectId: string,
  ): Promise<{
    assetId: string;
    strategy: UploadStrategy;
    uploadUrl?: string;
    uploadHeaders?: Record<string, string>;
    expiresAt?: Date;
    asset: AssetWithActions;
  }> {
    const strategy: UploadStrategy = file.size >= SIZE_THRESHOLD ? "SIGNED_URL" : "DIRECT";
    const client = useAssetClient();
    const authToken = config.getAuthToken?.();

    const request = createCreateAssetRequest({
      originalFilename: file.name,
      fileSize: BigInt(file.size),
      contentType: file.type,
      displayName: file.name,
      md5Hash: md5Hash,
      projectId,
      // TODO: Allow for a projectID (optional)
    });

    // Call the service with authentication
    const callOptions = useCallOptions(...(authToken ? [withBearerToken(authToken)] : []));

    const result = await client.createAsset(request, callOptions);

    if (result.isErr()) {
      throw new Error(`Failed to create asset: ${result.error.message}`);
    }

    const response: AssetService.CreateAssetResponse = result.value;

    return {
      assetId: response.asset!.data!.id,
      strategy: response.uploadStrategy as UploadStrategy,
      uploadUrl: response.uploadUrl || undefined,
      uploadHeaders: response.uploadHeaders || undefined,
      expiresAt: timestampToDateOptional(response.expiresAt),
      asset: response.asset,
    };
  }

  /**
   * Upload file directly via protobuf (< 10MB)
   */
  async function uploadFileDirect(uploadState: FileUploadState, assetId: string, md5Hash: string): Promise<void> {
    const client = useAssetClient();
    const authToken = config.getAuthToken?.();

    // Convert file to bytes
    const fileBytes = new Uint8Array(await uploadState.file.arrayBuffer());

    // Create upload request
    const request = createUploadAssetRequest({
      assetId: assetId,
      fileData: fileBytes,
      md5Hash: md5Hash,
      contentType: uploadState.file.type,
    });

    const callOptions = useCallOptions(...(authToken ? [withBearerToken(authToken)] : []));

    // Update progress to show upload starting
    setUploads((prev) =>
      prev.map((upload) =>
        upload.id === uploadState.id
          ? {
              ...upload,
              progress: {
                ...upload.progress,
                loaded: 0,
                total: uploadState.file.size,
                percentage: 0,
              },
            }
          : upload,
      ),
    );

    try {
      const result = await client.uploadAsset(request, callOptions);

      if (result.isErr()) {
        throw new Error(`Failed to upload asset: ${result.error.message}`);
      }

      const response: AssetService.UploadAssetResponse = result.value;

      // Update progress based on response
      if (response.progress) {
        setUploads((prev) =>
          prev.map((upload) =>
            upload.id === uploadState.id
              ? {
                  ...upload,
                  progress: {
                    ...upload.progress,
                    loaded: Number(response.progress!.bytesUploaded),
                    total: Number(response.progress!.totalBytes),
                    percentage: Math.round(response.progress!.percentage),
                  },
                  status: response.uploadComplete ? ("processing" as const) : ("uploading" as const),
                }
              : upload,
          ),
        );
      } else {
        // Fallback to 100% if no progress info
        setUploads((prev) =>
          prev.map((upload) =>
            upload.id === uploadState.id
              ? { ...upload, status: "processing" as const, progress: { ...upload.progress, percentage: 100 } }
              : upload,
          ),
        );
      }

      // Start polling for asset status if upload is complete
      if (response.uploadComplete && assetId) {
        console.log(`Direct upload complete for asset ${assetId}, starting polling`);
        // Notify that processing has started
        if (config.onAssetStatusChanged) {
          config.onAssetStatusChanged(assetId, "processing", null);
        }
        startGlobalPolling();
      } else {
        console.log(`Direct upload not complete yet. uploadComplete: ${response.uploadComplete}, assetId: ${assetId}`);
      }
    } catch (error) {
      // biome-ignore lint/complexity/noUselessCatch: <explanation>
      throw error;
    }
  }

  /**
   * Upload file via signed URL (>= 10MB)
   */
  async function uploadFileSignedUrl(uploadState: FileUploadState, md5Hash: string): Promise<void> {
    if (!uploadState.signedUrl) {
      throw new Error("Signed URL not available for large file upload");
    }

    const abortController = new AbortController();
    activeRequests.set(uploadState.id, abortController);

    try {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentage = Math.round((event.loaded / event.total) * 100);

          setUploads((prev) =>
            prev.map((upload) =>
              upload.id === uploadState.id
                ? {
                    ...upload,
                    progress: {
                      ...upload.progress,
                      loaded: event.loaded,
                      total: event.total,
                      percentage,
                    },
                  }
                : upload,
            ),
          );
        }
      });

      // Handle completion
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Notify control plane that upload is complete
          notifyUploadComplete(uploadState.assetId!, md5Hash, uploadState.file.size);
        } else {
          throw new Error(`Upload failed: ${xhr.statusText}`);
        }
      });

      // Handle errors
      xhr.addEventListener("error", () => {
        throw new Error("Upload failed due to network error");
      });

      // Handle abort
      abortController.signal.addEventListener("abort", () => {
        xhr.abort();
      });

      // Start upload
      xhr.open("PUT", uploadState.signedUrl);

      // Set required headers
      if (uploadState.uploadHeaders) {
        Object.entries(uploadState.uploadHeaders).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
      }

      xhr.send(uploadState.file);

      // Wait for completion
      await new Promise((resolve, reject) => {
        xhr.addEventListener("load", resolve);
        xhr.addEventListener("error", reject);
        xhr.addEventListener("abort", reject);
      });
    } finally {
      activeRequests.delete(uploadState.id);
    }
  }

  /**
   * Notify control plane that signed URL upload is complete via protobuf
   */
  async function notifyUploadComplete(assetId: string, md5Hash: string, fileSize: number): Promise<void> {
    const client = useAssetClient();
    const authToken = config.getAuthToken?.();

    const request = createCompleteAssetUploadRequest({
      assetId: assetId,
      md5Hash: md5Hash,
      fileSize: BigInt(fileSize),
    });

    const callOptions = useCallOptions(...(authToken ? [withBearerToken(authToken)] : []));

    const result = await client.completeAssetUpload(request, callOptions);

    if (result.isErr()) {
      throw new Error(`Failed to notify upload completion: ${result.error.message}`);
    }

    // Update status to processing and start polling
    setUploads((prev) =>
      prev.map((upload) => (upload.assetId === assetId ? { ...upload, status: "processing" as const } : upload)),
    );

    console.log(`Signed URL upload complete for asset ${assetId}, starting polling`);
    // Start polling for asset status
    startGlobalPolling();
  }

  /**
   * Bulk poll asset status using GetAssets endpoint to track processing progress for multiple assets
   */
  async function pollBulkAssetStatus(): Promise<void> {
    const client = useAssetClient();
    const authToken = config.getAuthToken?.();

    // Get all asset IDs that need polling (not completed or failed)
    const assetsToCheck = uploads().filter(
      (upload) => upload.assetId && upload.status !== "completed" && upload.status !== "error",
    );

    if (assetsToCheck.length === 0) {
      console.log("No assets to poll, stopping global polling");
      stopGlobalPolling();
      return;
    }

    const assetIds = assetsToCheck.map((upload) => upload.assetId!);
    console.log(`Bulk polling asset status for ${assetIds.length} assets:`, assetIds);

    try {
      const request = createGetAssetsRequest({
        assetIds: assetIds,
      });

      const callOptions = useCallOptions(...(authToken ? [withBearerToken(authToken)] : []));
      const result = await client.getAssets(request, callOptions);

      if (result.isErr()) {
        console.error("Failed to get assets:", result.error.message);
        return;
      }

      const response: AssetService.GetAssetsResponse = result.value;
      console.log(
        `Bulk polling response: found ${response.assets.length} assets, ${response.notFoundAssetIds.length} not found`,
      );

      // Process found assets
      const assetsCompletedOrFailed: string[] = [];

      setUploads((prev) =>
        prev.map((upload) => {
          if (!upload.assetId) return upload;

          // Check if asset was not found (user doesn't have access or asset doesn't exist)
          if (response.notFoundAssetIds.includes(upload.assetId)) {
            console.log(`Asset ${upload.assetId} not found or not accessible`);
            // Mark as error if not found
            if (upload.status !== "error") {
              if (config.onAssetStatusChanged) {
                config.onAssetStatusChanged(upload.assetId, "error", null);
              }
              assetsCompletedOrFailed.push(upload.assetId);
            }
            return {
              ...upload,
              status: "error" as const,
              error: "Asset not found or not accessible",
            };
          }

          // Find the asset in the response
          const assetWithActions = response.assets.find((a) => a.data?.id === upload.assetId);
          if (!assetWithActions?.data) return upload;

          const asset = assetWithActions.data;
          const isReady = asset.lifecycleState === Asset.AssetLifecycleState.READY;
          const isFailed = asset.lifecycleState === Asset.AssetLifecycleState.FAILED;
          const isProcessing =
            asset.lifecycleState === Asset.AssetLifecycleState.PROCESSING ||
            asset.lifecycleState === Asset.AssetLifecycleState.QUEUED_FOR_PROCESSING;
          const wasReady = upload.status === "completed";

          console.log(`Asset ${upload.assetId} lifecycle state:`, asset.lifecycleState);

          // Track assets that are completed or failed for cleanup
          if (isReady || isFailed) {
            assetsCompletedOrFailed.push(upload.assetId);
          }

          // Call onAssetReady callback when asset becomes ready for the first time
          if (isReady && !wasReady && config.onAssetReady) {
            config.onAssetReady(upload.assetId);
          }

          // Notify status changes
          if (config.onAssetStatusChanged) {
            if (isReady && !wasReady) {
              config.onAssetStatusChanged(upload.assetId, "completed", assetWithActions);
            } else if (isFailed && upload.status !== "error") {
              config.onAssetStatusChanged(upload.assetId, "error", assetWithActions);
            } else if (isProcessing && upload.status !== "processing") {
              config.onAssetStatusChanged(upload.assetId, "processing", assetWithActions);
            }
          }

          return {
            ...upload,
            lifecycleState: asset.lifecycleState,
            status: isReady ? "completed" : isFailed ? "error" : isProcessing ? "processing" : upload.status,
            error: isFailed ? "Asset processing failed" : upload.error,
            progress: isReady ? { ...upload.progress, percentage: 100 } : upload.progress,
          };
        }),
      );

      // Log completed/failed assets
      if (assetsCompletedOrFailed.length > 0) {
        console.log(`Assets completed or failed: ${assetsCompletedOrFailed.join(", ")}`);
      }
    } catch (error) {
      console.error("Error bulk polling asset status:", error);
    }
  }

  /**
   * Start global polling for all assets that need status updates
   */
  function startGlobalPolling(): void {
    // Skip polling during SSR
    if (isServer) {
      return;
    }

    // Don't start if already polling
    if (globalPollingTimer !== null) {
      console.log("Global polling already active");
      return;
    }

    console.log("Starting global asset polling");

    const poll = () => {
      pollBulkAssetStatus()
        .then(() => {
          // Continue polling if not stopped and there are assets to poll
          if (globalPollingTimer !== null) {
            const assetsToCheck = uploads().filter(
              (upload) => upload.assetId && upload.status !== "completed" && upload.status !== "error",
            );

            if (assetsToCheck.length > 0) {
              globalPollingTimer = window.setTimeout(poll, 3000);
            } else {
              console.log("No more assets to poll, stopping global polling");
              stopGlobalPolling();
            }
          }
        })
        .catch((error) => {
          console.error("Error in global polling:", error);
          // Stop polling on error
          stopGlobalPolling();
        });
    };

    // Start immediate poll
    globalPollingTimer = window.setTimeout(poll, 3000);
  }

  /**
   * Stop global polling
   */
  function stopGlobalPolling(): void {
    // Skip during SSR
    if (isServer) {
      return;
    }

    if (globalPollingTimer !== null) {
      console.log("Stopping global polling");
      window.clearTimeout(globalPollingTimer);
      globalPollingTimer = null;
    }
  }

  /**
   * Stop polling for asset status
   */
  function stopAssetStatusPolling(assetId: string): void {
    // Skip during SSR
    if (isServer) {
      return;
    }

    const timerId = activePollingTimers.get(assetId);
    if (timerId) {
      console.log(`Clearing polling timer for asset ${assetId}`);
      window.clearTimeout(timerId);
      activePollingTimers.delete(assetId);
    } else {
      console.log(`No polling timer found for asset ${assetId}`);
    }
  }

  /**
   * Process a single file upload
   */
  async function processFileUpload(uploadState: FileUploadState): Promise<void> {
    try {
      // Update status to uploading
      setUploads((prev) =>
        prev.map((upload) => (upload.id === uploadState.id ? { ...upload, status: "uploading" as const } : upload)),
      );

      // Use pre-computed MD5 hash or compute it if not available
      let md5Hash = uploadState.md5Hash;
      if (!md5Hash) {
        md5Hash = await computeMD5Checksum(uploadState.file);
      }

      // Create asset
      const assetData = await createAsset(uploadState.file, md5Hash, uploadState.projectId);

      // Update upload state with asset info
      setUploads((prev) =>
        prev.map((upload) =>
          upload.id === uploadState.id
            ? {
                ...upload,
                assetId: assetData.assetId,
                strategy: assetData.strategy,
                signedUrl: assetData.uploadUrl,
                uploadHeaders: assetData.uploadHeaders,
                expiresAt: assetData.expiresAt,
              }
            : upload,
        ),
      );

      // Call onAssetCreated callback when asset gets an ID
      console.log(`Asset created with ID: ${assetData.assetId}, strategy: ${assetData.strategy}`);
      if (config.onAssetCreated) {
        config.onAssetCreated(assetData.assetId, assetData.asset);
      }

      // Notify that upload is starting
      if (config.onAssetStatusChanged) {
        config.onAssetStatusChanged(assetData.assetId, "uploading", null);
      }

      // Upload file based on strategy
      if (assetData.strategy === "DIRECT") {
        await uploadFileDirect(uploadState, assetData.assetId, md5Hash);
      } else {
        await uploadFileSignedUrl(uploadState, md5Hash);
      }
    } catch (error) {
      console.error("File upload failed:", error);
      setUploads((prev) =>
        prev.map((upload) =>
          upload.id === uploadState.id
            ? {
                ...upload,
                status: "error" as const,
                error: error instanceof Error ? error.message : "Upload failed",
              }
            : upload,
        ),
      );
    }
  }

  /**
   * Upload multiple files
   */
  const uploadFiles = async (files: File[], projectId: string): Promise<void> => {
    // Validate files and check for duplicates
    const validFiles: File[] = [];
    const errors: string[] = [];
    const duplicates: string[] = [];

    for (const file of files) {
      const validationError = validateFile(file, config);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
        continue;
      }

      // Check for duplicates against existing uploads
      const isDupe = await isDuplicateFile(file, uploads());
      if (isDupe) {
        duplicates.push(file.name);
        continue;
      }

      validFiles.push(file);
    }

    if (errors.length > 0) {
      console.error("File validation errors:", errors);
      // TODO: Show validation errors to user
    }

    if (duplicates.length > 0) {
      console.log("Duplicate files skipped:", duplicates);
      // TODO: Show duplicate file notifications to user
    }

    if (validFiles.length === 0) {
      if (duplicates.length > 0) {
        console.log("All files were duplicates, nothing to upload");
      }
      return;
    }

    // Create upload states with MD5 hash computation
    const newUploads: FileUploadState[] = await Promise.all(
      validFiles.map(async (file) => {
        let md5Hash: string | undefined;
        try {
          md5Hash = await computeMD5Checksum(file);
        } catch (error) {
          console.warn(`Failed to compute MD5 for ${file.name}:`, error);
          md5Hash = undefined;
        }

        return {
          id: generateUploadId(),
          file,
          status: "pending" as const,
          projectId,
          strategy: file.size >= SIZE_THRESHOLD ? "SIGNED_URL" : "DIRECT",
          progress: {
            total: file.size,
            loaded: 0,
            percentage: 0,
            speed: 0,
            timeRemaining: 0,
          },
          md5Hash,
        };
      }),
    );

    // Add to uploads list
    setUploads((prev) => [...prev, ...newUploads]);

    // Process uploads with concurrency limit
    const semaphore = new Array(config.maxConcurrent || 3).fill(null);
    const uploadPromises = newUploads.map(async (uploadState) => {
      // Wait for available slot
      await Promise.race(
        semaphore.map(async (_, index) => {
          while (semaphore[index] !== null) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
          semaphore[index] = uploadState.id;
        }),
      );

      try {
        await processFileUpload(uploadState);
      } finally {
        const index = semaphore.indexOf(uploadState.id);
        if (index !== -1) {
          semaphore[index] = null;
        }
      }
    });

    await Promise.allSettled(uploadPromises);
  };

  /**
   * Remove a file from upload queue
   */
  const removeFile = (uploadId: string): void => {
    // Cancel if uploading
    const controller = activeRequests.get(uploadId);
    if (controller) {
      controller.abort();
      activeRequests.delete(uploadId);
    }

    // Stop polling if active
    const upload = uploads().find((u) => u.id === uploadId);
    if (upload?.assetId) {
      stopAssetStatusPolling(upload.assetId);
    }

    setUploads((prev) => prev.filter((upload) => upload.id !== uploadId));
  };

  /**
   * Retry a failed upload
   */
  const retryUpload = async (uploadId: string): Promise<void> => {
    const upload = uploads().find((u) => u.id === uploadId);
    if (!upload || upload.status !== "error") {
      return;
    }

    // Reset upload state
    setUploads((prev) =>
      prev.map((u) =>
        u.id === uploadId
          ? {
              ...u,
              status: "pending",
              error: undefined,
              progress: { ...u.progress, loaded: 0, percentage: 0 },
            }
          : u,
      ),
    );

    // Retry upload
    await processFileUpload(upload);
  };

  /**
   * Clear all completed uploads
   */
  const clearCompleted = (): void => {
    setUploads((prev) => prev.filter((upload) => upload.status !== "completed" && upload.status !== "error"));
  };

  /**
   * Cancel an active upload
   */
  const cancelUpload = (uploadId: string): void => {
    const controller = activeRequests.get(uploadId);
    if (controller) {
      controller.abort();
    }
    removeFile(uploadId);
  };

  // Cleanup on unmount
  onCleanup(() => {
    activeRequests.forEach((controller) => controller.abort());
    activeRequests.clear();

    // Clear all polling timers (only on client)
    if (!isServer) {
      activePollingTimers.forEach((timerId) => window.clearTimeout(timerId));
      activePollingTimers.clear();

      // Stop global polling
      stopGlobalPolling();
    }
  });

  return {
    uploads,
    isUploading,
    totalProgress,
    uploadFiles,
    removeFile,
    retryUpload,
    clearCompleted,
    cancelUpload,
  };
}
